import styled from '@emotion/styled'
import { useRouterState } from '@tanstack/react-router'
import { useContext } from 'react'

import { A } from './A'
import { Flex } from './Flex'
import { ProfileMenu } from './ProfileMenu'
import { Breadcrumbs } from './Breadcrumbs'

import { ArrowIcon, AvatarIcon, Logo, LogoSmall } from '@/assets/icons'
import MobileSidebar from '@/components/Sidebar/MobileSidebar'
import { ItemConfig } from '@/constants'
import { colors } from '@/constants/colors'
import { mediaQuery } from '@/utils/mediaQuery'
import { findChild } from '@/utils/subMenu'
import { AuthContext } from '@/features/Auth/application/AuthContext'
import { ProviderSwitcher } from '@/features/Auth/components/ProviderSwitcher'
import { CaretIcon } from './StyledIcon'
import { OutlineButton } from './Button'

const StyledWrapper = styled(Flex)`
  transition-property: opacity;
  transition-duration: 250ms;
  margin: 1rem;
  margin-bottom: 0;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`

export const Headline = styled.h3`
  font-size: 17px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.255px;
  margin: 0;
`

const Header = styled(Flex)`
  height: 5rem;
  border-bottom: 0.5px solid ${colors.darkBlue4};
  position: sticky;
  top: 0;
  background-color: ${colors.darkBlue1};
  z-index: 100;
  justify-content: center;
  align-items: center;

  ${mediaQuery('sm')} {
    display: none;
  }
}`

const MobileHeader = styled(Flex)`
  position: sticky;
  top: 0;
  background-color: ${colors.darkBlue1};
  z-index: 100;
  align-items: center;
  justify-content: space-between;

  display: none;
  ${mediaQuery('sm')} {
    display: flex;
    padding: 16px 10px 15px 10px;
  }
`

const TitleWrapper = styled(Flex)`
  align-items: center;
`

const ProfileWrapper = styled(Flex)`
  gap: 1.25rem;
`

const MobileHeaderTitle = () => {
  return (
    <>
      <A href="/">
        <LogoSmall />
      </A>
      <ProfileWrapper>
        <MobileSidebar />
      </ProfileWrapper>
    </>
  )
}

export default function AppHeader({
  location,
  routeConfig: parent,
}: {
  location: string
  routeConfig?: ItemConfig
  isLoading?: boolean
}) {
  const { authStatus } = useContext(AuthContext)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const child = parent?.submenu ? findChild(location, parent.submenu) : null
  const current = child ?? parent
  const title = current?.label ?? parent?.label

  if (authStatus === 'signedIn') {
    return (
      <>
        <Header id="header">
          <Flex
            py="1.125rem"
            px="1.875rem"
            width="100%"
            justifyContent="space-between"
          >
            <TitleWrapper>
              <Breadcrumbs />
            </TitleWrapper>
            <ProfileWrapper alignItems="center">
              <ProviderSwitcher containerId="header" />
              <ProfileMenu
                containerId="header"
                trigger={({ open, setOpen }) => (
                  <Flex
                    gap="10px"
                    alignItems="center"
                    onClick={() => setOpen(!open)}
                    style={{ cursor: 'pointer' }}
                  >
                    <AvatarIcon width="36px" />
                    <CaretIcon
                      as={ArrowIcon}
                      rotate={open}
                      tabIndex={0}
                      role="button"
                      aria-label="Open profile menu"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setOpen(!open)
                        }
                      }}
                    />
                  </Flex>
                )}
              />
            </ProfileWrapper>
          </Flex>
        </Header>
        <MobileHeader>
          <MobileHeaderTitle />
        </MobileHeader>
      </>
    )
  }

  return (
    <StyledWrapper>
      <Flex width="100%" justifyContent="space-between" alignItems="center">
        <A href="/">
          <Logo />
        </A>
        <Flex flexDirection="row" alignItems="center" gap="1rem">
          <Headline as="span">Don&#39;t have an account yet?</Headline>
          <A href="https://www.fluence.network/become-a-provider">
            <OutlineButton>Join waitlist</OutlineButton>
          </A>
        </Flex>
      </Flex>
    </StyledWrapper>
  )
}
